import { DateTime } from "luxon";
import { z } from "zod";

import { formatDateMmDdYyyy, getLocalDateMmDdYyyy, normalizeSimpleDate } from "@/src/lib/utils/date-and-phone-utils";
import { Classification } from "@/src/models/constants/classifications";
import { RecordRequestSource } from "@/src/models/constants/record-request-source";
import { FileType } from "@/src/models/file";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";

export const CONFLUENT_ALL_STAR_TIMEZONE = "America/Los_Angeles";

const CONFLUENT_ALL_STAR_EMAIL_ADDRESSES = {
  medicalRecords: "medicalrecords@goconfluent.com",
  credentialing: "credentialingsupport@goconfluent.com",
  grievances: "grievances@allstarphysicaltherapy.org",
  // Payer / insurance bulletins (non-patient-specific) are emailed to All Star, never synced.
  insuranceBulletins: "cinman@allstarphysicaltherapy.org",
  allStarMedicalRecords: "medicalrecords@allstarphysicaltherapy.org",
} as const;

const RECIPIENT_BY_CLASSIFICATION: Partial<Record<Classification, string>> = {
  [Classification.LEGAL_SUBPOENA]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.medicalRecords,
  [Classification.ADMIN_CREDENTIAL]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.credentialing,
  [Classification.LEGAL_GRIEVANCE]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.grievances,
  // ADMIN is used here as the payer-bulletin bucket (see classification-prompts.ts / document-mappings.ts).
  [Classification.ADMIN]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.insuranceBulletins,
  [Classification.ADMIN_RISK_ASSESSMENT]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.allStarMedicalRecords,
};

export const CONFLUENT_ALL_STAR_EMAIL_ONLY_CLASSIFICATIONS = new Set<Classification>([
  Classification.LEGAL_SUBPOENA,
  Classification.ADMIN_CREDENTIAL,
  Classification.LEGAL_GRIEVANCE,
  Classification.ADMIN,
  Classification.ADMIN_RISK_ASSESSMENT,
]);

export function getConfluentAllStarEmailRecipients(section: Section): string[] {
  const classification = section.uploadClassification || section.classification;
  const recipient = RECIPIENT_BY_CLASSIFICATION[classification];

  return recipient ? [recipient] : [];
}

// Recipient for insurance communications whose patient is not found in Clinicient (emailed to All Star
// from the INSURANCE_COMMUNICATION workflow instead of being filed to a chart).
export const CONFLUENT_ALL_STAR_INSURANCE_COMMUNICATION_EMAIL = CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.insuranceBulletins;

const receivedDateSubjectSchema = z.object({ dateReceived: z.string().nullish() });

const SUBJECT_LABEL_BY_CLASSIFICATION: Partial<Record<Classification, string>> = {
  [Classification.INSURANCE_COMMUNICATION]: "Insurance Communication",
  [Classification.LEGAL_GRIEVANCE]: "Patient Grievance",
  [Classification.LEGAL_SUBPOENA]: "Subpoena",
  [Classification.ADMIN]: "Bulletin Update",
  [Classification.ADMIN_CREDENTIAL]: "Credentialing",
  [Classification.ADMIN_RISK_ASSESSMENT]: "Insurance Risk Assessment",
};

// Legal docs (subpoenas / grievances) key their subject off the INBOUND file ingest date; every other
// email-routed classification uses the extracted received date from the reviewed content.
const INGEST_DATE_SUBJECT_CLASSIFICATIONS = new Set<Classification>([
  Classification.LEGAL_GRIEVANCE,
  Classification.LEGAL_SUBPOENA,
]);

function resolveExtractedReceivedDatePrefix(section: Section): string {
  const reviewed = receivedDateSubjectSchema.safeParse(section.reviewedContent ?? {});
  const parsedDate = reviewed.success
    ? z.iso.date().safeParse(normalizeSimpleDate(reviewed.data.dateReceived?.trim()))
    : null;

  return parsedDate?.success ? `${formatDateMmDdYyyy(parsedDate.data)} ` : "";
}

function resolveIngestDatePrefix(task: Pick<Task, "createdAt" | "files">): string {
  const inboundFile = task.files.find((file) => file.type === FileType.INBOUND);
  const ingest = DateTime.fromISO(inboundFile?.createdAt || task.createdAt);

  return ingest.isValid ? `${getLocalDateMmDdYyyy(CONFLUENT_ALL_STAR_TIMEZONE, ingest)} ` : "";
}

export function getConfluentAllStarDocRoutingEmailSubject(
  section: Section,
  task: Pick<Task, "createdAt" | "files">
): string | null {
  const classification = section.uploadClassification || section.classification;
  const label = SUBJECT_LABEL_BY_CLASSIFICATION[classification];

  if (!label) return null;

  const prefix = INGEST_DATE_SUBJECT_CLASSIFICATIONS.has(classification)
    ? resolveIngestDatePrefix(task)
    : resolveExtractedReceivedDatePrefix(section);

  return `${prefix}${label}`;
}

export type ConfluentAllStarRecordRequestRoute = "third_party" | "provider" | "needs_review";

export function resolveConfluentAllStarRecordRequestRoute(section: Section): ConfluentAllStarRecordRequestRoute {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;

  switch (reviewedContent.recordRequestSource) {
    case RecordRequestSource.OTHER:
      return "third_party";
    case RecordRequestSource.WORKERS_COMP:
    case RecordRequestSource.HOSPITAL:
    case RecordRequestSource.CLINIC:
      return "provider";
    default:
      return "needs_review";
  }
}
